package fr.labodoc.healthProfessional.webApp.data.source.remote

import arrow.core.Either
import arrow.core.raise.catch
import arrow.core.raise.either
import fr.labodoc.healthProfessional.api.client.ApiClient
import fr.labodoc.healthProfessional.api.payloads.responses.ApplicationInformationResponse
import fr.labodoc.healthProfessional.api.payloads.responses.SelfResponse
import fr.labodoc.healthProfessional.webApp.data.error.RemoteDataSourceError
import fr.labodoc.healthProfessional.webApp.data.model.HealthDirectoryHealthProfessionalModel
import fr.labodoc.healthProfessional.webApp.data.model.HealthProfessionalUserModel
import fr.labodoc.domain.labodoc.InputFile
import fr.labodoc.domain.labodoc.common.NotificationFrequency
import fr.labodoc.healthProfessional.api.payloads.requests.ChangePasswordRequest
import fr.labodoc.healthProfessional.api.payloads.requests.FinalizePasswordResetRequest
import fr.labodoc.healthProfessional.api.payloads.requests.FreestandingApplicationRequest
import fr.labodoc.healthProfessional.api.payloads.requests.LoginRequest
import fr.labodoc.healthProfessional.api.payloads.requests.MedicalCardCPFRegistrationRequest
import fr.labodoc.healthProfessional.api.payloads.requests.MedicalCardCPSRegistrationRequest
import fr.labodoc.healthProfessional.api.payloads.requests.PasswordResetRequest
import fr.labodoc.healthProfessional.api.payloads.requests.RegisterSelfFcmRegistrationTokenRequest
import fr.labodoc.healthProfessional.api.payloads.requests.UpdateSelfMedicalInterestsRequest
import fr.labodoc.healthProfessional.api.payloads.requests.UpdateSelfNotificationsSettingsRequest

class UserRemoteDataSourceImpl(
  private val apiClient: ApiClient
) : UserRemoteDataSource {
  override suspend fun login(
    emailAddress: String,
    password: String,
    rememberMe: Boolean
  ): Either<RemoteDataSourceError, HealthProfessionalUserModel> =
    either {
      catch({
        apiClient
          .login(
            payload = LoginRequest(
                email = emailAddress,
                password = password,
                rememberMe = rememberMe
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .map(SelfResponse::toModel)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

    override suspend fun logout(
    ): Either<RemoteDataSourceError, Unit> =
      either {
        catch({
          apiClient
            .logout()
            .mapLeft(RemoteDataSourceError::SomethingWentWrong)
            .bind()
        }) { exception: Exception ->
          raise(RemoteDataSourceError.UnreachableServer(exception))
        }
      }

  override suspend fun deleteAccount(
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .deleteAccount()
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun sendPasswordResetRequest(
    emailAddress: String
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .sendPasswordResetRequest(
            payload = PasswordResetRequest(
                emailAddress = emailAddress
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun finalizePasswordResetRequest(
    token: String,
    password: String
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .finalizePasswordResetRequest(
            token = token,
            payload = FinalizePasswordResetRequest(
                password = password
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun changePassword(
    previousPassword: String,
    newPassword: String
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .changePassword(
            payload = ChangePasswordRequest(
                previousPassword = previousPassword,
                newPassword = newPassword
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun getSelf(
  ): Either<RemoteDataSourceError, HealthProfessionalUserModel> =
    either {
      catch({
        apiClient
          .getSelf()
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .map(SelfResponse::toModel)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun registerFcmRegistrationToken(
    token: String
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .registerFcmRegistrationToken(
            payload = RegisterSelfFcmRegistrationTokenRequest(
                token = token
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun deleteFcmRegistrationToken(
    token: String
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .deleteFcmRegistrationToken(
            token = token
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun updateMedicalInterests(
    medicalInterestIds: List<String>
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .updateMedicalInterests(
            payload = UpdateSelfMedicalInterestsRequest(
                medicalInterestIds = medicalInterestIds
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun updateNotificationsSettings(
    notificationFrequency: NotificationFrequency?
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .updateNotificationsSettings(
            payload = UpdateSelfNotificationsSettingsRequest(
                notificationFrequency = notificationFrequency
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun getHealthProfessionalByCardNumber(
    medicalCardNumber: String
  ): Either<RemoteDataSourceError, HealthDirectoryHealthProfessionalModel> =
    either {
      catch({
        apiClient
          .getHealthProfessionalInformationApplicationByCardNumber(
            medicalCardNumber = medicalCardNumber
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .map(ApplicationInformationResponse::toModel)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun registerWithMedicalCardCPS(
    medicalCardNumber: String,
    firstName: String,
    lastName: String,
    healthDirectoryProfessionCode: String,
    healthDirectoryProfessionalCategoryCode: String,
    healthDirectoryProfessionalStatusCode: String,
    healthDirectoryExpertiseCode: String?,
    healthDirectoryPharmacistSectionCode: String?,
    labodocMedicalProfessionId: String,
    labodocMedicalSpecialityId: String,
    labodocMedicalInterestIds: List<String>,
    departmentCode: String,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    partnerCode: String?
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .registerWithMedicalCardCPS(
            payload = MedicalCardCPSRegistrationRequest(
                medicalCardNumber = medicalCardNumber,
                firstName = firstName,
                lastName = lastName,
                healthDirectoryProfessionCode = healthDirectoryProfessionCode,
                healthDirectoryProfessionalCategoryCode = healthDirectoryProfessionalCategoryCode,
                healthDirectoryProfessionalStatusCode = healthDirectoryProfessionalStatusCode,
                healthDirectoryExpertiseCode = healthDirectoryExpertiseCode,
                healthDirectoryPharmacistSectionCode = healthDirectoryPharmacistSectionCode,
                labodocMedicalProfessionId = labodocMedicalProfessionId,
                labodocMedicalSpecialityId = labodocMedicalSpecialityId,
                labodocMedicalInterestIds = labodocMedicalInterestIds,
                departmentCode = departmentCode,
                notificationFrequency = notificationFrequency,
                email = emailAddress,
                password = password,
                partnerCode = partnerCode
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun registerWithMedicalCardCPF(
    rppsNumber: String?,
    medicalCardNumber: String,
    firstName: String,
    lastName: String,
    healthDirectoryProfessionCode: String,
    medicalDiplomaId: String,
    medicalInterestIds: List<String>,
    universityHospitalId: String,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    partnerCode: String?
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .registerWithMedicalCardCPF(
            payload = MedicalCardCPFRegistrationRequest(
                rppsNumber = rppsNumber,
                medicalCardNumber = medicalCardNumber,
                firstName = firstName,
                lastName = lastName,
                healthDirectoryProfessionCode = healthDirectoryProfessionCode,
                medicalDiplomaId = medicalDiplomaId,
                medicalInterestIds = medicalInterestIds,
                universityHospitalId = universityHospitalId,
                notificationFrequency = notificationFrequency,
                email = emailAddress,
                password = password,
                partnerCode = partnerCode
            )
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun registerWithoutMedicalCardHealthProfessional(
    rppsNumber: String?,
    firstName: String,
    lastName: String,
    phoneNumber: String,
    medicalSpecialityId: String,
    medicalInterestIds: List<String>,
    facilityName: String,
    facilityUnit: String,
    facilityCity: String,
    facilityDepartmentCode: String,
    supportingDocument: InputFile,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    comment: String?,
    partnerCode: String?
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .registerWithoutMedicalCard(
            payload = FreestandingApplicationRequest.HealthProfessional(
              rppsNumber = rppsNumber,
              firstName = firstName,
              lastName = lastName,
              phoneNumber = phoneNumber,
              medicalSpeciality = medicalSpecialityId,
              medicalInterests = medicalInterestIds,
              facilityName = facilityName,
              facilityUnit = facilityUnit,
              facilityCity = facilityCity,
              facilityDepartmentCode = facilityDepartmentCode,
              notificationFrequency = notificationFrequency,
              emailAddress = emailAddress,
              password = password,
              comment = comment,
              partnerCode = partnerCode
            ),
            supportingDocument = supportingDocument
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun registerWithoutMedicalCardExtern(
    firstName: String,
    lastName: String,
    universityHospitalId: String,
    supportingDocument: InputFile,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    comment: String?,
    partnerCode: String?
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .registerWithoutMedicalCard(
            payload = FreestandingApplicationRequest.Extern(
              firstName = firstName,
              lastName = lastName,
              universityHospitalId = universityHospitalId,
              notificationFrequency = notificationFrequency,
              emailAddress = emailAddress,
              password = password,
              comment = comment,
              partnerCode = partnerCode
            ),
            supportingDocument = supportingDocument
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }

  override suspend fun confirmEmail(
    token: String
  ): Either<RemoteDataSourceError, Unit> =
    either {
      catch({
        apiClient
          .validateApplicationEmail(
            token = token
          )
          .mapLeft(RemoteDataSourceError::SomethingWentWrong)
          .bind()
      }) { exception: Exception ->
        raise(RemoteDataSourceError.UnreachableServer(exception))
      }
    }
}

private fun SelfResponse.toModel(): HealthProfessionalUserModel =
  HealthProfessionalUserModel(
    emailAddress = this.user.emailAddress,
    lastLoginDate = this.user.lastLoginDate,
    rppsNumber = this.user.rppsNumber,
    firstName = this.user.firstName,
    lastName = this.user.lastName,
    department = HealthProfessionalUserModel.Department(
      code = this.user.department.code,
      name = this.user.department.name
    ),
    medicalProfession = this.user.medicalProfession?.let { medicalProfession ->
      HealthProfessionalUserModel.MedicalProfession(
        id = medicalProfession.id,
        name = medicalProfession.name
      )
    },
    medicalSpeciality = this.user.medicalSpeciality?.let { medicalSpeciality ->
      HealthProfessionalUserModel.MedicalSpeciality(
        id = medicalSpeciality.id,
        name = medicalSpeciality.name
      )
    },
    medicalInterests = this.user.medicalInterests
      .map {
        HealthProfessionalUserModel.MedicalInterest(
          id = it.id,
          name = it.name
        )
      }
      .toSet(),
    canHaveMedicalInterests = this.user.canHaveMedicalInterests,
    professionalCategory = HealthProfessionalUserModel.ProfessionalCategory(
      code = this.user.professionalCategory.code,
      name = this.user.professionalCategory.name
    ),
    professionalStatus = this.user.professionalStatus?.let { professionalStatus ->
      HealthProfessionalUserModel.ProfessionalStatus(
        code = professionalStatus.code,
        name = professionalStatus.name
      )
    },
    notificationFrequency = this.user.notificationFrequency
  )

private fun ApplicationInformationResponse.toModel(): HealthDirectoryHealthProfessionalModel =
  HealthDirectoryHealthProfessionalModel(
    nationalIdentifier = this.healthProfessional.nationalIdentifier,
    rppsNumber = this.healthProfessional.rppsNumber,
    medicalCard = HealthDirectoryHealthProfessionalModel.MedicalCard(
      typeCode = this.healthProfessional.medicalCard.typeCode,
      typeName = this.healthProfessional.medicalCard.typeName,
      number = this.healthProfessional.medicalCard.number
    ),
    situations = this.healthProfessional.situations
      .map { situation ->
        HealthDirectoryHealthProfessionalModel.Situation(
          courtesyCode = situation.courtesyCode,
          lastName = situation.lastName,
          firstName = situation.firstName,
          healthDirectoryProfession = HealthDirectoryHealthProfessionalModel.Situation.HealthDirectoryProfession(
            code = situation.healthDirectoryProfession.code,
            name = situation.healthDirectoryProfession.name
          ),
          healthDirectoryProfessionalCategory = HealthDirectoryHealthProfessionalModel.Situation.HealthDirectoryProfessionalCategory(
            code = situation.healthDirectoryProfessionalCategory.code,
            name = situation.healthDirectoryProfessionalCategory.name
          ),
          healthDirectoryProfessionalStatus = HealthDirectoryHealthProfessionalModel.Situation.HealthDirectoryProfessionalStatus(
            code = situation.healthDirectoryProfessionalStatus.code,
            name = situation.healthDirectoryProfessionalStatus.name
          ),
          healthDirectoryExpertise = situation.healthDirectoryExpertise?.let { expertise ->
            HealthDirectoryHealthProfessionalModel.Situation.HealthDirectoryExpertise(
              code = expertise.code,
              name = expertise.name
            )
          },
          healthDirectoryPharmacistTableSectionCode = situation.healthDirectoryPharmacistTableSectionCode,
          facility = situation.facility?.let { facility ->
            HealthDirectoryHealthProfessionalModel.Situation.Facility(
              name = facility.name,
              officeCedex = facility.officeCedex,
              departmentCode = facility.departmentCode
            )
          },
          labodocMatchings = situation.labodocMatchings
            .map { labodocMatching ->
              HealthDirectoryHealthProfessionalModel.Situation.LabodocMatching(
                medicalProfession = HealthDirectoryHealthProfessionalModel.Situation.LabodocMatching.MedicalProfession(
                  id = labodocMatching.medicalProfession.id,
                  name = labodocMatching.medicalProfession.name
                ),
                medicalSpeciality = HealthDirectoryHealthProfessionalModel.Situation.LabodocMatching.MedicalSpeciality(
                  id = labodocMatching.medicalSpeciality.id,
                  name = labodocMatching.medicalSpeciality.name
                ),
                medicalInterests = labodocMatching.medicalInterests
                  .map { labodocMedicalInterest ->
                    HealthDirectoryHealthProfessionalModel.Situation.LabodocMatching.MedicalInterest(
                      id = labodocMedicalInterest.id,
                      name = labodocMedicalInterest.name
                    )
                  }
              )
            }
        )
      }
  )
